// the gallery has multiple features attached to it:
// - supports multiple images
// - has an inline image preview, tenatively they're collapsed to a bar line
// - it can be expanded into an overlay
//   - the overlay may have a different left / right mechanism
// - the navigational elements can be below the image itself
// - the media may be an image or a video

import { type CSSProperties, css, cx } from '@linaria/core'
import { type FC, type HTMLProps, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '#components/Icon'
import { Modal } from '#components/Modal'
import CloseIcon from '#icons/close-24.svg'
import ExpandIcon from '#icons/new-tab-24.svg'
import { style } from '#styles/index'
import type { DeepPartial } from '#types/DeepPartial'
import { FullscreenGallery } from './FullscreenGallery'
import { Indicator } from './Indicator'
import { MediaList } from './MediaList'

export type GalleryItemId = string
export type GalleryItem =
  | {
      type: 'image'
      id: string
      url: string
      alt?: string
    }
  | {
      type: 'video'
      id: string
      url: string
      alt?: string
      muted?: boolean
      autoplay?: boolean
      loop?: boolean
      controls?: boolean
    }

type GalleryOptions = {
  // lazy: maybe?

  /**
   * if the navigation is enabled, where is the navigation element located?
   * @default 'below'
   */
  selector: 'below' | 'within' | 'off'
  /**
   * can the gallery be expanded into an overlay?
   * @default false
   */
  canFullscreen: boolean
  /**
   * How wide is the gap between elements (in px)
   * @default 8
   */
  gap: number
}

const galleryClass = css`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  --gallery-gap: 8px;

  > .expander {
    position: absolute;
    top: 0;
    right: 0;
    margin: 12px;
    cursor: pointer;
    color: ${style.theme.textAccent};
    transition: 0.25s ease color;

    &:hover {
      color: ${style.theme.textAccent};
    }
  }
`

export type GalleryItemSetter =
  | {
      currentItem: GalleryItemId
      onCurrentItemChange: (id: GalleryItemId) => void
    }
  | {
      currentItem?: undefined
      onCurrentItemChange?: undefined
    }

const scrollIntoViewOptions = { behavior: 'smooth', block: 'center', inline: 'center', container: 'nearest' } as const

const galleryOverlayContentClass = css`
  position: relative;

  > .icon {
    position: absolute;
    top: 0;
    right: 0;
    margin: 12px;

    cursor: pointer;
    color: ${style.theme.textAccent};
  }
`
export const GalleryOverlay: FC<GalleryProperties & { open: boolean; onOpenChange: (open: boolean) => void }> = ({
  open,
  onOpenChange,
  ...props
}) => {
  return (
    <Modal lifted open={open} onOpenChange={onOpenChange} contentClassName={galleryOverlayContentClass}>
      <Gallery {...props} />
      <Icon className="icon" iconUrl={CloseIcon} size="l" onClick={() => onOpenChange(false)} />
    </Modal>
  )
}

export type GalleryProperties = { items: GalleryItem[] } & DeepPartial<GalleryOptions> & HTMLProps<HTMLDivElement> & GalleryItemSetter
export const Gallery: FC<GalleryProperties> = ({ ref, ...props }) => {
  const {
    items,
    currentItem: externalCurrentItem,
    onCurrentItemChange: externalOnCurrentItemChange,
    selector: selectorPosition = 'below',
    canFullscreen = false,
    gap = 8,
    className,
    style,

    ...htmlProps
  } = props
  const [internalCurrentItemId, setInternalCurrentItemId] = useState<GalleryItemId | undefined>(items.at(0)?.id)
  const currentItemId = externalCurrentItem ?? internalCurrentItemId
  const setCurrentItemId = externalOnCurrentItemChange ?? setInternalCurrentItemId
  const currentItemIndex = useMemo(() => items.findIndex((x) => x.id === currentItemId), [currentItemId, items])

  const listRef = useRef<HTMLDivElement>(null)
  const indicators = useRef<(HTMLDivElement | null)[]>([])
  const itemRefs = useRef<(HTMLImageElement | HTMLVideoElement | null)[]>([])
  const [expanded, setExpanded] = useState(false)

  const onIndicatorClicked = useCallback(
    (index: number) => {
      const element = items.at(index)
      if (element) {
        itemRefs.current.at(index)?.scrollIntoView(scrollIntoViewOptions)
        setCurrentItemId(element.id)
        // setVideoPlayState('stopped')
      }
    },
    [items, setCurrentItemId],
  )

  const onPrevClicked = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation()
      if (currentItemIndex > 0) {
        onIndicatorClicked(currentItemIndex - 1)
      }
    },
    [currentItemIndex, onIndicatorClicked],
  )

  const onNextClicked = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation()
      onIndicatorClicked(currentItemIndex + 1)
    },
    [currentItemIndex, onIndicatorClicked],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: only on initial mount effect
  useLayoutEffect(() => {
    itemRefs.current.at(currentItemIndex)?.scrollIntoView({ ...scrollIntoViewOptions, behavior: 'instant' })
  }, [])

  return (
    <div
      ref={ref}
      className={cx(galleryClass, 'gallery', selectorPosition === 'within' && 'indicator-within', className)}
      style={{ ...style, '--gallery-gap': `${gap}px` } as CSSProperties}
      {...htmlProps}
    >
      <MediaList
        items={items}
        currentIndex={currentItemIndex}
        ref={listRef}
        onItemFocused={(_index, item) => {
          setCurrentItemId(item.id)
        }}
        onItemSelected={(index, _item) => {
          for (let i = 0; i < indicators.current.length; i += 1) {
            const indicator = indicators.current.at(i)
            if (!indicator) {
              continue
            }
            const enabled = i === index
            if (enabled) {
              indicator.classList.add('selected')
            } else {
              indicator.classList.remove('selected')
            }
          }
        }}
        onItemRef={(i, instance) => {
          itemRefs.current[i] = instance
        }}
      />

      {selectorPosition !== 'off' && (
        <Indicator
          onPreviousRequested={onPrevClicked}
          onNextRequested={onNextClicked}
          onIndicatorClicked={(_evt, i) => onIndicatorClicked(i)}
          itemCount={items.length}
          currentIndex={currentItemIndex}
        />
      )}

      {canFullscreen && <Icon className="expander" iconUrl={ExpandIcon} size="l" onClick={() => setExpanded(true)} />}
      {canFullscreen && <FullscreenGallery items={items} open={expanded} onOpenChange={setExpanded} />}
    </div>
  )
}
